import React, { useState, useEffect } from 'react'
import {Heart, MessageCircleDashed as MessageCircle, Send, LogIn} from 'lucide-react'
import { motion } from 'framer-motion'
import { lumi } from '../lib/lumi'
import TestimonialsWall from '../components/TestimonialsWall'
import toast from 'react-hot-toast'

const Testimonials: React.FC = () => {
  const [showForm, setShowForm] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [formData, setFormData] = useState({ name: '', message: '' })
  const [testimonials, setTestimonials] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [user, setUser] = useState<any>(null)
  const [checkingAuth, setCheckingAuth] = useState(true)

  useEffect(() => {
    fetchTestimonials()
    checkAuthentication()

    // Listener para mudanças de autenticação
    const unsubscribe = lumi.auth.onAuthChange(({ isAuthenticated, user: authUser }) => {
      setUser(authUser)
      setCheckingAuth(false)
    })

    return unsubscribe
  }, [])

  const checkAuthentication = async () => {
    try {
      const currentUser = lumi.auth.user
      setUser(currentUser)
    } catch (error) {
      console.error('Erro ao verificar autenticação:', error)
    } finally {
      setCheckingAuth(false)
    }
  }

  const fetchTestimonials = async () => {
    setLoading(true)
    try {
      const { list } = await lumi.entities.testimonials.list({
        filter: { status: { $in: ['APPROVED', 'aprovado'] } },
        sort: { createdAt: -1 }
      })
      // Deduplicar por _id para evitar registros duplicados
      const seen = new Set<string>()
      const unique = list.filter((t: any) => {
        if (seen.has(t._id)) return false
        seen.add(t._id)
        return true
      })
      setTestimonials(unique)
    } catch (error) {
      console.error('Erro ao buscar testemunhos:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!user) {
      toast.error('Você precisa estar logado para enviar um depoimento')
      return
    }

    // Validação de campos obrigatórios
    if (!formData.name.trim() || !formData.message.trim()) {
      toast.error('Por favor, preencha todos os campos obrigatórios')
      return
    }

    // Sanitização de inputs
    const sanitizeInput = (input: string): string => {
      return input
        .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
        .replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '')
        .replace(/on\w+\s*=\s*["'][^"']*["']/gi, '')
        .replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, '')
        .trim()
    }
    
    setSubmitting(true)
    
    try {
      await lumi.entities.testimonials.create({
        name: formData.name,
        email: user.email || '',
        message: formData.message,
        status: 'PENDING',
        creator: user.userId || 'authenticated',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      })
      
      toast.success('Testemunho enviado e aguardando aprovação!')
      setShowForm(false)
      setFormData({ name: '', message: '' })
    } catch (error) {
      console.error('Erro ao enviar depoimento:', error)
      toast.error('Erro ao enviar depoimento. Tente novamente.')
    } finally {
      setSubmitting(false)
    }
  }

  const handleLogin = async () => {
    try {
      await lumi.auth.signIn()
    } catch (error) {
      console.error('Erro ao fazer login:', error)
      toast.error('Erro ao fazer login')
    }
  }



  return (
    <div className="min-h-screen">
      {/* Header */}
      <section className="bg-gradient-to-br from-[#1e3a8a] to-[#6b8f71] py-20">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-5xl font-bold text-white mb-6">
              Depoimentos
            </h1>
            <p className="text-xl text-white/90">
              Testemunhos de vidas transformadas pelo poder de Deus
            </p>
          </motion.div>
        </div>
      </section>

      {/* Mural de Depoimentos */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <TestimonialsWall 
            testimonials={testimonials} 
            loading={loading} 
            showAll={true}
          />

          {/* Formulário de Depoimento */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="mt-16"
          >
            {!showForm ? (
              <div className="bg-gradient-to-r from-[#1e3a8a] to-[#6b8f71] rounded-xl p-8 text-center">
                <h3 className="text-2xl font-bold text-white mb-4">
                  Compartilhe Seu Testemunho
                </h3>
                <p className="text-white/90 mb-6">
                  Sua história pode inspirar e fortalecer a fé de outras pessoas
                </p>
                {checkingAuth ? (
                  <div className="flex justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                  </div>
                ) : user ? (
                  <button
                    onClick={() => setShowForm(true)}
                    className="bg-white text-[#1e3a8a] px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-all hover:scale-105 shadow-lg"
                  >
                    Enviar Depoimento
                  </button>
                ) : (
                  <div className="space-y-4">
                    <p className="text-white/80 text-sm">
                      Faça login para compartilhar seu testemunho
                    </p>
                    <button
                      onClick={handleLogin}
                      className="bg-white text-[#1e3a8a] px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-all hover:scale-105 shadow-lg inline-flex items-center gap-2"
                    >
                      <LogIn size={20} />
                      Fazer Login
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-200">
                <h3 className="text-2xl font-bold text-[#1e3a8a] mb-6">
                  Compartilhe Seu Testemunho
                </h3>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                      Seu Nome
                    </label>
                    <input
                      id="name"
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Como você gostaria de ser identificado"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1e3a8a] focus:border-transparent"
                      disabled={submitting}
                      required
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                      Seu Testemunho
                    </label>
                    <textarea
                      id="message"
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      placeholder="Compartilhe como Deus tem agido em sua vida..."
                      rows={6}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1e3a8a] focus:border-transparent resize-none"
                      disabled={submitting}
                      required
                    />
                  </div>

                  <div className="flex gap-3">
                    <button
                      type="submit"
                      disabled={submitting}
                      className="flex-1 bg-[#1e3a8a] text-white py-3 px-6 rounded-lg font-semibold hover:bg-[#152d6b] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {submitting ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                          Enviando...
                        </>
                      ) : (
                        <>
                          <Send size={18} />
                          Enviar Depoimento
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowForm(false)
                        setFormData({ name: '', message: '' })
                      }}
                      disabled={submitting}
                      className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors disabled:opacity-50"
                    >
                      Cancelar
                    </button>
                  </div>

                  <p className="text-sm text-gray-500 text-center">
                    Seu depoimento será revisado antes de ser publicado
                  </p>
                </form>
              </div>
            )}
          </motion.div>
        </div>
      </section>
    </div>
  )
}

export default Testimonials
